import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { parapheurCircuitPath } from '../../services/signature/parapheurRoutes';
import { toast } from 'sonner';
import { Eye, FilePlus, FolderInput, FolderOpen, FolderPlus, LayoutTemplate, RotateCcw, Trash2, Upload, X } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { canAccessFeature, asCollaboratorRole } from '../../services/auth/permissions';
import {
  useDeleteFolder,
  useDeleteFile,
  useSetFileVisibility,
  useAllDossierFiles,
  useAllDossierFolders,
  useMoveFile,
} from '../../hooks/queries/useDossierFiles';
import { useDossierDrafts, useDiscardDraft } from '../../hooks/queries/useDocumentGeneration';
import { FILE_TABLE_GRID, FileRow } from './FileRow';
import { ListEmpty, ListToolbar, Segments, ToolbarGrow, ToolbarSearch } from '../ui/ListToolbar';
import { DocumentFolderSidebar } from './DocumentFolderSidebar';
import { CreateFolderModal } from './CreateFolderModal';
import { ApplyFolderTemplateModal } from './ApplyFolderTemplateModal';
import { RenameFolderModal } from './RenameFolderModal';
import { canApplyFolderTemplate } from '../../lib/folderTemplates';
import { buildDocumentTree } from '../../lib/documentTree';
import { shouldMoveFile } from '../../lib/fileDrop';
import { DossierEmailsTab } from '../dossiers/DossierEmailsTab';
import { useDossierEmails } from '../../hooks/queries/useGmailInbox';
import { useDossierGraftEmails } from '../../hooks/queries/useDossierGraftEmails';
import {
  ALL_DOCUMENTS_ID,
  ancestorFolderIds,
  filesForNavSelection,
  findFolderNode,
  isAllDocumentsNav,
  isEmailNav,
  navIdFromSearch,
  uploadFolderId,
} from '../../lib/dossiers/documentNav';
import { VIRTUAL_EMAIL_LABEL } from '../../lib/dossiers/virtualEmailFolder';
import { FilePreviewModal } from './FilePreviewModal';
import { FileDropzone } from './FileDropzone';
import { MoveFileModal } from './MoveFileModal';
import { CreateCircuitModal } from '../signature/CreateCircuitModal';
import { createPortal } from 'react-dom';
import { LetterComposer } from '../documents/LetterComposer';
import { Button } from '../ui/Button';
import { getDownloadUrl } from '../../services/files/filesService';
import { filterFiles } from '../../services/files/fileUtils';
import type { DossierFile, Folder } from '../../services/files/filesService';
import type { DocumentDraft } from '../../services/documents/documentService';

interface Props {
  dossierId: string;
}

export function DossierFilesTab({ dossierId }: Props) {
  const navigate = useNavigate();
  const collaborator = useAuthStore((s) => s.collaborator);
  const role = asCollaboratorRole(collaborator?.role);
  const canEditGlobal = role ? canAccessFeature(role, 'files_edit') : false;
  const canCreateCircuit = role ? canAccessFeature(role, 'signature_circuit_create') : false;
  const canGenerate = role ? canAccessFeature(role, 'documents_generate') : false;

  const [letterOpen, setLetterOpen] = useState(false);
  const [resumeDraft, setResumeDraft] = useState<DocumentDraft | null>(null);
  const [draftToDelete, setDraftToDelete] = useState<DocumentDraft | null>(null);
  const { data: drafts = [] } = useDossierDrafts(canGenerate ? dossierId : undefined);
  const discardDraft = useDiscardDraft(dossierId);

  const [createFolderParentId, setCreateFolderParentId] = useState<string | null>(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<Folder | null>(null);
  const [previewFile, setPreviewFile] = useState<DossierFile | null>(null);
  const [moveTarget, setMoveTarget] = useState<DossierFile | null>(null);
  const [createCircuitFor, setCreateCircuitFor] = useState<{
    id: string;
    name: string;
    kind: 'visa' | 'validation';
  } | null>(null);
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'shared_client'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(() => new Set());
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const importRef = useRef<(() => void) | null>(null);
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(() => navIdFromSearch(searchParams.toString()));
  const searching = searchQuery.trim() !== '';

  useEffect(() => {
    if (navIdFromSearch(searchParams.toString()) === ALL_DOCUMENTS_ID) return;
    setSelectedId(navIdFromSearch(searchParams.toString()));
  }, [searchParams]);

  const { data: allFolders = [], isLoading: foldersLoading } = useAllDossierFolders(dossierId);
  const { data: allFiles = [] } = useAllDossierFiles(dossierId);
  const { data: gmailEmails } = useDossierEmails(dossierId);
  const { data: graftEmails } = useDossierGraftEmails(dossierId);
  const emailCount = (gmailEmails?.length ?? 0) + (graftEmails?.length ?? 0);
  const deleteFolder = useDeleteFolder(dossierId);
  const deleteFile = useDeleteFile(dossierId);
  const setVisibility = useSetFileVisibility(dossierId);
  const moveFile = useMoveFile(dossierId);

  const visibleFiles = useMemo(
    () => (visibilityFilter === 'shared_client'
      ? allFiles.filter((f) => f.visibility === 'shared_client')
      : allFiles),
    [allFiles, visibilityFilter],
  );
  const tree = useMemo(
    () => buildDocumentTree(allFolders, visibleFiles),
    [allFolders, visibleFiles],
  );

  useEffect(() => {
    if (!uploadFolderId(selectedId)) return;
    if (!findFolderNode(tree.rootFolders, selectedId)) setSelectedId(ALL_DOCUMENTS_ID);
  }, [tree, selectedId]);

  const paneFiles = searching
    ? filterFiles(visibleFiles, searchQuery)
    : filesForNavSelection(selectedId, visibleFiles);
  const dropTarget = uploadFolderId(selectedId);
  const paneLabel = searching
    ? `Recherche « ${searchQuery.trim()} »`
    : isEmailNav(selectedId)
      ? VIRTUAL_EMAIL_LABEL
      : isAllDocumentsNav(selectedId)
        ? 'Tous les documents'
        : (findFolderNode(tree.rootFolders, selectedId)?.folder.name ?? 'Dossier');
  const paneCount = searching
    ? paneFiles.length
    : isEmailNav(selectedId)
      ? emailCount
      : paneFiles.length;

  function toggleFolder(folderId: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }

  function expandFolder(folderId: string) {
    setExpandedIds((prev) => {
      if (prev.has(folderId)) return prev;
      const next = new Set(prev);
      next.add(folderId);
      return next;
    });
  }

  function selectNav(id: string) {
    setSelectedId(id);
    const parentIds = ancestorFolderIds(tree.rootFolders, id);
    if (parentIds.length === 0) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      for (const a of parentIds) next.add(a);
      return next;
    });
  }

  async function handleDownload(file: DossierFile) {
    try {
      const url = await getDownloadUrl(file.id);
      window.open(url, '_blank');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur téléchargement');
    }
  }

  async function handleDeleteFile(file: DossierFile) {
    if (!confirm(`Supprimer définitivement « ${file.filename} » ?`)) return;
    try {
      await deleteFile.mutateAsync(file.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur suppression');
    }
  }

  async function handleToggleVisibility(file: DossierFile) {
    const next = file.visibility === 'shared_client' ? 'internal' : 'shared_client';
    try {
      await setVisibility.mutateAsync({ fileId: file.id, visibility: next });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur');
    }
  }

  async function handleDeleteFolder(folder: Folder) {
    if (!confirm(`Supprimer le dossier « ${folder.name} » ? Il doit être vide.`)) return;
    try {
      await deleteFolder.mutateAsync(folder.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erreur — le dossier n'est peut-être pas vide.");
    }
  }

  async function handleInternalMove(fileId: string, fromFolderId: string | null, toFolderId: string | null) {
    if (!shouldMoveFile(fromFolderId, toFolderId)) return;
    try {
      await moveFile.mutateAsync({ fileId, toFolderId });
      if (toFolderId) expandFolder(toFolderId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Impossible de déplacer le fichier.');
    }
  }

  const showApplyTemplate =
    canEditGlobal
    && !foldersLoading
    && canApplyFolderTemplate(allFolders.length);

  const selectedFiles = paneFiles.filter((f) => selectedFileIds.has(f.id));
  const allSelected = paneFiles.length > 0 && selectedFiles.length === paneFiles.length;
  const folderNameOf = (file: DossierFile) =>
    file.folder_id && file.folder_id !== dropTarget
      ? (allFolders.find((f) => f.id === file.folder_id)?.name ?? null)
      : null;

  function toggleFile(file: DossierFile) {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(file.id)) next.delete(file.id);
      else next.add(file.id);
      return next;
    });
  }

  async function shareSelection(visibility: 'shared_client' | 'internal') {
    try {
      for (const file of selectedFiles) {
        if (file.visibility !== visibility) await setVisibility.mutateAsync({ fileId: file.id, visibility });
      }
      setSelectedFileIds(new Set());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Impossible de modifier le partage.');
    }
  }

  // Les e-mails ont leur propre entrée : « Tous les documents » ne liste que les fichiers.
  const showsEmails = !searching && isEmailNav(selectedId);
  const showsTable = !isEmailNav(selectedId) || searching;

  return (
    <div>
      <FileDropzone
        bare
        openRef={importRef}
        dossierId={dossierId}
        folderId={dropTarget}
        disabled={!canEditGlobal}
        onMoveFile={handleInternalMove}
        onUploadedToFolder={expandFolder}
      >
        <div className="flex min-h-[480px] overflow-clip rounded-xl border border-neutral-200 bg-white">
          <DocumentFolderSidebar
            tree={tree}
            selectedId={selectedId}
            onSelect={selectNav}
            expandedIds={expandedIds}
            onToggle={toggleFolder}
            emailCount={emailCount}
            allCount={visibleFiles.length}
            canEdit={canEditGlobal}
            onRenameFolder={setRenameTarget}
            onDeleteFolder={handleDeleteFolder}
            onCreateSubfolder={(folder) => {
              setCreateFolderParentId(folder.id);
              setCreateFolderOpen(true);
              expandFolder(folder.id);
            }}
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex flex-col gap-2.5 border-b border-[#e6e2d8] px-[18px] pb-3 pt-3.5">
              <p className="flex items-baseline gap-2 text-sm text-cime-text-muted">
                <FolderOpen className="h-4 w-4 self-center text-cime-primary" aria-hidden />
                <b className="text-[15px] font-semibold text-cime-text-primary">{paneLabel}</b>
                <span className="text-[13px]">
                  · {paneCount} élément{paneCount > 1 ? 's' : ''}
                </span>
              </p>
              <ListToolbar>
                <ToolbarSearch
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Rechercher dans le dossier…"
                  ariaLabel="Rechercher un document"
                  className="w-[240px] min-w-[180px]"
                />
                <Segments
                  label="Partage"
                  value={visibilityFilter}
                  onChange={setVisibilityFilter}
                  options={[
                    { value: 'all', label: 'Tous' },
                    { value: 'shared_client', label: 'Partagés client' },
                  ]}
                />
                <ToolbarGrow />
                {showApplyTemplate && (
                  <ToolButton onClick={() => setApplyTemplateOpen(true)}>
                    <LayoutTemplate className="h-3.5 w-3.5" aria-hidden /> Appliquer une arborescence
                  </ToolButton>
                )}
                {canEditGlobal && (
                  <ToolButton
                    onClick={() => {
                      setCreateFolderParentId(dropTarget);
                      setCreateFolderOpen(true);
                    }}
                  >
                    <FolderPlus className="h-3.5 w-3.5" aria-hidden /> Nouveau dossier
                  </ToolButton>
                )}
                {canEditGlobal && (
                  <ToolButton onClick={() => importRef.current?.()}>
                    <Upload className="h-3.5 w-3.5" aria-hidden /> Importer
                  </ToolButton>
                )}
                {canGenerate && (
                  <ToolButton
                    primary
                    onClick={() => {
                      setResumeDraft(null);
                      setLetterOpen(true);
                    }}
                  >
                    <FilePlus className="h-3.5 w-3.5" aria-hidden /> Générer un document
                  </ToolButton>
                )}
              </ListToolbar>
            </div>

            {drafts.length > 0 && (
              <div className="border-b border-[#e6e2d8] bg-[#fbfaf6] px-4 py-2.5">
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400">
                  Brouillons en cours
                </p>
                <ul className="space-y-1">
                  {drafts.map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-2 text-[13px]">
                      <span className="truncate text-cime-text-primary">{d.title}</span>
                      <span className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          className="flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-xs text-cime-text-primary hover:border-neutral-300"
                          onClick={() => {
                            setResumeDraft(d);
                            setLetterOpen(true);
                          }}
                        >
                          <RotateCcw className="h-3 w-3" aria-hidden /> Reprendre
                        </button>
                        <button
                          type="button"
                          className="flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-xs text-cime-text-muted hover:text-red-600"
                          onClick={() => setDraftToDelete(d)}
                        >
                          <Trash2 className="h-3 w-3" aria-hidden /> Supprimer
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-auto">
              {showsEmails && <DossierEmailsTab dossierId={dossierId} embedded />}
              {!showsTable ? null : searching && paneFiles.length === 0 ? (
                <ListEmpty>Aucun fichier ne correspond à « {searchQuery} ».</ListEmpty>
              ) : paneFiles.length === 0 ? (
                <ListEmpty>
                  Aucun document dans « {paneLabel} ». Importez un fichier ou glissez-le sur la fenêtre.
                </ListEmpty>
              ) : (
                <>
                  <div
                    className={
                      FILE_TABLE_GRID +
                      ' sticky top-0 z-[1] border-b border-[#e6e2d8] bg-white py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400'
                    }
                  >
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={allSelected}
                      aria-label="Tout sélectionner"
                      onClick={() =>
                        setSelectedFileIds(allSelected ? new Set() : new Set(paneFiles.map((f) => f.id)))
                      }
                      className={
                        'grid h-4 w-4 place-items-center rounded border-[1.5px] ' +
                        (selectedFiles.length > 0
                          ? 'border-cime-primary bg-cime-primary text-white'
                          : 'border-neutral-300 bg-white')
                      }
                    >
                      {selectedFiles.length > 0 ? (
                        <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden>
                          <path
                            d={allSelected ? 'M2.2 6.2 4.8 8.8 9.8 3.2' : 'M2.5 6h7'}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : null}
                    </button>
                    <span>Titre</span>
                    <span>Source</span>
                    <span>Client</span>
                    <span>Modifié</span>
                    <span />
                    <span />
                  </div>
                  {paneFiles.map((file) => (
                    <FileRow
                      key={file.id}
                      file={file}
                      table={{
                        selected: selectedFileIds.has(file.id),
                        onSelect: toggleFile,
                        folderName: folderNameOf(file),
                      }}
                      canEdit={canEditGlobal}
                      onPreview={setPreviewFile}
                      onDownload={handleDownload}
                      onDelete={handleDeleteFile}
                      onMove={setMoveTarget}
                      onToggleVisibility={handleToggleVisibility}
                      canCreateCircuit={canCreateCircuit}
                      onStartCircuit={(f, kind) =>
                        setCreateCircuitFor({ id: f.id, name: f.filename, kind: kind ?? 'visa' })
                      }
                      onOpenCircuit={(f) => {
                        if (f.signature_circuit_id) navigate(parapheurCircuitPath(f.signature_circuit_id));
                      }}
                    />
                  ))}
                </>
              )}
            </div>

            {selectedFiles.length > 0 && canEditGlobal && (
              <div className="m-3 flex flex-wrap items-center gap-2 rounded-full bg-cime-primary px-4 py-2 text-[13px] text-white">
                <b className="mr-1 font-semibold">
                  {selectedFiles.length} sélectionné{selectedFiles.length > 1 ? 's' : ''}
                </b>
                <BulkButton onClick={() => shareSelection('shared_client')}>
                  <Eye className="h-3.5 w-3.5" aria-hidden /> Partager au client
                </BulkButton>
                <BulkButton onClick={() => shareSelection('internal')}>Ne plus partager</BulkButton>
                <BulkButton onClick={() => setBulkMoveOpen(true)}>
                  <FolderInput className="h-3.5 w-3.5" aria-hidden /> Déplacer
                </BulkButton>
                <span className="flex-1" />
                <button
                  type="button"
                  onClick={() => setSelectedFileIds(new Set())}
                  title="Tout désélectionner"
                  aria-label="Tout désélectionner"
                  className="grid h-6 w-6 place-items-center rounded-full hover:bg-white/15"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
            )}
          </div>
        </div>
      </FileDropzone>

      {bulkMoveOpen && selectedFiles[0] && (
        <MoveFileModal
          dossierId={dossierId}
          file={selectedFiles[0]}
          others={selectedFiles.slice(1)}
          onClose={() => {
            setBulkMoveOpen(false);
            setSelectedFileIds(new Set());
          }}
        />
      )}

      {createFolderOpen && (
        <CreateFolderModal
          dossierId={dossierId}
          parentId={createFolderParentId}
          onClose={() => setCreateFolderOpen(false)}
        />
      )}

      {applyTemplateOpen && (
        <ApplyFolderTemplateModal
          dossierId={dossierId}
          onClose={() => setApplyTemplateOpen(false)}
        />
      )}

      {renameTarget && (
        <RenameFolderModal
          dossierId={dossierId}
          folder={renameTarget}
          onClose={() => setRenameTarget(null)}
        />
      )}

      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}

      {moveTarget && (
        <MoveFileModal dossierId={dossierId} file={moveTarget} onClose={() => setMoveTarget(null)} />
      )}

      {createCircuitFor && (
        <CreateCircuitModal
          fileId={createCircuitFor.id}
          fileName={createCircuitFor.name}
          kind={createCircuitFor.kind}
          onClose={() => setCreateCircuitFor(null)}
          onCreated={(id) => {
            const kind = createCircuitFor.kind;
            setCreateCircuitFor(null);
            if (kind === 'visa') navigate(parapheurCircuitPath(id));
          }}
        />
      )}

      {letterOpen && (
        <LetterComposer
          dossierId={dossierId}
          existingDraft={resumeDraft}
          onClose={() => { setLetterOpen(false); setResumeDraft(null); }}
        />
      )}

      {draftToDelete && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-cime-modal
        >
          <div className="bg-white rounded-cime shadow-xl max-w-md w-full">
            <div className="px-5 py-4 border-b border-neutral-200 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              <h2 className="text-base font-semibold text-cime-text-primary">
                Supprimer le brouillon
              </h2>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-cime-text-primary">
                Voulez-vous vraiment supprimer{' '}
                <span className="font-medium">« {draftToDelete.title} »</span> ?
              </p>
            </div>
            <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2">
              <Button
                variant="ghost"
                type="button"
                onClick={() => setDraftToDelete(null)}
                disabled={discardDraft.isPending}
              >
                Annuler
              </Button>
              <Button
                type="button"
                onClick={() => {
                  discardDraft.mutate(draftToDelete.id, {
                    onSuccess: () => setDraftToDelete(null),
                    onError: (e) => toast.error(e instanceof Error ? e.message : 'Échec.'),
                  });
                }}
                disabled={discardDraft.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {discardDraft.isPending ? 'Suppression…' : 'Supprimer'}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function ToolButton({
  onClick,
  primary,
  children,
}: {
  onClick: () => void;
  primary?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[13px] font-medium transition-colors ' +
        (primary
          ? 'bg-cime-primary text-white hover:opacity-90'
          : 'border border-neutral-200 bg-white text-cime-text-primary hover:border-neutral-300')
      }
    >
      {children}
    </button>
  );
}

function BulkButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-7 items-center gap-1.5 rounded-full bg-white/15 px-3 font-medium hover:bg-white/25"
    >
      {children}
    </button>
  );
}
